import type { AxiosInstance } from 'axios';
import type {
  AdBannerKind, AdBannerSetting, AdBannerStatsResponse, BookingRequestResponse, BookingStage, BookingStatus,
  CreateOrganizerRequest, EventPromotionRow, Feedback, FeedbackPlatform, FeedbackStats, Organizer,
  OrganizerKind, OrganizerMember, OrganizerPortalSummary, OrganizerVisibility, PageResponse, PriceItem, PriceItemRequest, PromotionTier,
  UpdateOrganizerRequest,
} from '../types';

/**
 * Abrufe der Verwaltung, Bereich Veranstalter & Verkauf: Veranstalter, Buchungen, Preise, Werbeplatz, Feedback.
 * Einmal für Web und App; die App ruft sie über `create…Service(api)` mit ihrer eigenen Axios-Instanz.
 * Typen, die es in `types.ts` noch nicht gibt, stehen hier im Bereich.
 *
 * Pfade und Rollen wie im Web (`organizerService`, `bookingService`, `adBannerService`,
 * `eventService.getFeedback`). Alles hier steht ADMIN und MODERATOR offen, mit einer Ausnahme:
 * `zahlungErhalten` ist Admin allein — die Oberfläche bietet den Knopf Moderatoren gar nicht an,
 * denn ein 403 löst in der App einen Token-Refresh aus.
 */

/** So lange trägt ein frisch verifizierter Veranstalter in der Redaktionsliste „Neu“. */
export const VERANSTALTER_NEU_TAGE = 14;

/** Ist der Veranstalter in den letzten `VERANSTALTER_NEU_TAGE` Tagen verifiziert worden? Web und App. */
export const istNeuVerifiziert = (o: Pick<Organizer, 'verifiedSince'>, jetzt: number = Date.now()): boolean =>
  !!o.verifiedSince && jetzt - new Date(o.verifiedSince).getTime() < VERANSTALTER_NEU_TAGE * 86_400_000;

/** Zuletzt verifizierte zuerst, Veranstalter ohne Verwalter ans Ende (dort nach Name). */
export const nachVerifiziertSeit = (a: Pick<Organizer, 'verifiedSince' | 'name'>, b: Pick<Organizer, 'verifiedSince' | 'name'>): number => {
  const ta = a.verifiedSince ? new Date(a.verifiedSince).getTime() : -Infinity;
  const tb = b.verifiedSince ? new Date(b.verifiedSince).getTime() : -Infinity;
  return ta === tb ? a.name.localeCompare(b.name, 'de') : tb - ta;
};

/** Antwort auf „Zahlung erhalten", wenn nur das Freischalten scheiterte (409). */
export interface ZahlungTeilerfolg {
  /** Warum noch nicht freigeschaltet wurde, etwa „Event liegt noch in der Prüfung". */
  grund: string;
  /** Die Bestellung im neuen Stand (jetzt „Zahlung erhalten"). */
  bestellung: BookingRequestResponse | null;
}

/** Ergebnis von „Zahlung erhalten": ganz geklappt, oder Geld vermerkt und Freischalten offen. */
export type ZahlungErgebnis =
  | { art: 'freigeschaltet'; bestellung: BookingRequestResponse }
  | ({ art: 'zahlung-vermerkt' } & ZahlungTeilerfolg);

/**
 * Vor „Zahlung erhalten“ und „Rechnung nachholen“: ist der Veranstalter noch nicht eingeordnet,
 * entsteht ohne Einordnung keine Rechnung. Vorschlag ist die Zielgruppe des bestellten Preises;
 * die Oberfläche fragt nach, statt still zu buchen. `null`, wenn nichts zu fragen ist.
 */
export function einordnungVorRechnung(
  b: Pick<BookingRequestResponse, 'organizerUnclassified' | 'bookedAudience' | 'organizerName'>,
): { orgKind: OrganizerKind; hinweis: string } | null {
  if (!b.organizerUnclassified) return null;
  const orgKind: OrganizerKind = b.bookedAudience === 'COMMERCIAL' ? 'COMMERCIAL' : 'CLUB';
  const als = orgKind === 'CLUB' ? 'Verein' : 'kommerzieller Anbieter';
  return {
    orgKind,
    hinweis: `${b.organizerName ?? 'Der Veranstalter'} ist noch nicht eingeordnet und wird dabei als ${als} eingeordnet. `
      + 'Ohne Einordnung gibt es keine Rechnung.',
  };
}

/**
 * Was nach „Zahlung erhalten“ mit der Rechnung passiert ist, in einem Satz. Steht im Toast bzw.
 * in der Meldung, damit ein Ausfall nicht hinter „freigeschaltet“ verschwindet.
 */
export function rechnungsStand(b: BookingRequestResponse | null | undefined): { ok: boolean; text: string } | null {
  if (!b) return null;
  if (b.invoiceNumber) return { ok: true, text: `Rechnung ${b.invoiceNumber} verschickt` };
  if (b.invoiceMissing) return { ok: false, text: `Keine Rechnung: ${b.invoiceError ?? 'Grund unbekannt'}` };
  return null;
}

/**
 * Stand einer Bestellung. Der Server rechnet ihn (`bookingStageOf`); Bestandszeilen ohne `stage`
 * bekommen ihn hier aus denselben drei Angaben, wie im Web (`AdminBookings.stageOf`).
 */
export const bestellStand = (b: BookingRequestResponse): BookingStage =>
  b.stage ?? (b.status === 'APPLIED' ? 'ACTIVATED'
    : b.status === 'DECLINED' ? 'DECLINED'
    : b.status === 'CANCELLED' ? 'CANCELLED'
    : b.paidAt ? 'PAID'
    : b.invoiceNumber ? 'INVOICE_SENT' : 'AWAITING_PAYMENT');

/**
 * Namen der Anzeigen im Werbeplatz. Gestaltung und Ziel stehen im Web
 * (`frontend/src/components/adBanners.tsx`, dort auch die Labels); die App zeigt nur Name und
 * Satz, deshalb diese Kopie.
 */
export const WERBEPLATZ_ANZEIGEN: { key: string; label: string; description: string }[] = [
  { key: 'delta-bike', label: 'Delta Bike (Equipment-Partner)', description: 'Partner-Anzeige mit Logo. Die ganze Fläche führt zum Shop von Delta Bike.' },
  { key: 'organizer-promo', label: 'Für Veranstalter („Mehr Leute am Start“)', description: 'Eigene Anzeige. Führt auf die Seite „Für Veranstalter“.' },
  { key: 'partner-promo', label: 'Hier werben („Deine Marke. Mitten im Radsport“)', description: 'Eigene Anzeige. Führt auf die Seite „Für Partner“, Anfragen stehen unter dem Thema „Werbeplatz“.' },
  { key: 'winterchallenge', label: 'Winterchallenge (Kilometer sammeln)', description: 'Eigene Anzeige mit dem Stand der Community. Führt auf die Seite der Winterchallenge.' },
];

/** Wo eine Anzeige steht, je Art (`ad_banners.kind`, V194); Wortlaut für Web und App. */
export const AD_BANNER_KIND_LABELS: Record<AdBannerKind, { label: string; hint: string }> = {
  ANNOUNCEMENT: { label: 'Ankündigung', hint: 'Steht über den Events und kommt zuerst. Laufende Werbung wechselt sich danach mit ihr ab.' },
  AD: { label: 'Werbung', hint: 'Steht am Computer über den Events, am Handy zwischen den Events. Läuft eine Ankündigung, kommt sie oben im Wechsel nach ihr.' },
};

/** Name einer Anzeige, auch einer früheren: `werbeplatzLabel` aus `./auswertung`. */

export interface FeedbackFilter {
  platform?: FeedbackPlatform;
  minRating?: number;
  withComment?: boolean;
}

export function createVerkaufService(api: AxiosInstance) {
  const daten = <T>(p: Promise<{ data: T }>): Promise<T> => p.then(r => r.data);

  return {
    // ── Veranstalter ────────────────────────────────────────────────────────────────────
    /** Redaktionsliste mit Kontaktdaten, Verwaltern, Premium und Einordnung. */
    veranstalter: (search?: string) =>
      daten<Organizer[]>(api.get('/organizers/admin', { params: search ? { search } : {} })),
    veranstalterEinzeln: (id: number) => daten<Organizer>(api.get(`/organizers/${id}/full`)),
    veranstalterAnlegen: (req: CreateOrganizerRequest) => daten<Organizer>(api.post('/organizers', req)),
    /**
     * Stammdaten speichern. Verifizierung, Sichtbarkeit, Freigabe-Automatik und hochgeladene
     * Bilder haben eigene Schreibwege und stehen nicht im Request.
     */
    veranstalterSpeichern: (id: number, req: UpdateOrganizerRequest) =>
      daten<Organizer>(api.put(`/organizers/${id}`, req)),
    /** Nur die Einordnung, eigener Endpunkt wie in der Web-Liste. */
    einordnen: (id: number, orgKind: OrganizerKind) =>
      daten<Organizer>(api.put(`/organizers/${id}/kind`, { orgKind })),
    veranstalterLoeschen: (id: number) => api.delete(`/organizers/${id}`).then(() => undefined),
    /** Alle Verknüpfungen eines Veranstalters (aktive, beantragte, entzogene). */
    zugaengeVon: (organizerId: number) =>
      daten<OrganizerMember[]>(api.get(`/organizer-access/by-organizer/${organizerId}`)),
    // ── Selbstpflege (Veranstalter-Bereich) ─────────────────────────────────────────────
    // Logo, Banner und Sichtbarkeit haben nur Schreibwege für **verknüpfte** Konten
    // (`OrganizerPortalService`, `requireMember`); für alle anderen antwortet der Server 404.
    // Die Verwaltung ruft sie deshalb nur, wenn `meineVeranstalter` den Veranstalter enthält.
    /** Mit welchen Veranstaltern ist das angemeldete Konto verknüpft? Für jedes Konto erlaubt. */
    meineVeranstalter: () => daten<OrganizerPortalSummary[]>(api.get('/organizer-portal/me')),
    /** Stammdaten samt Sichtbarkeit und wartenden Bildern, wie der Veranstalter sie sieht. */
    portalProfil: (organizerId: number) => daten<Organizer>(api.get(`/organizer-portal/${organizerId}/profile`)),
    /** Die sechs Sichtbarkeits-Schalter, sofort wirksam (eigener Schreibweg, kein Vorschlag). */
    sichtbarkeitSetzen: (organizerId: number, v: OrganizerVisibility) =>
      daten<Organizer>(api.put(`/organizer-portal/${organizerId}/visibility`, v)),
    /** Hochgeladenes Logo bzw. Banner entfernen (auch ein wartendes). */
    bildEntfernen: (organizerId: number, art: 'logo' | 'banner') =>
      daten<Organizer>(api.delete(`/organizer-portal/${organizerId}/${art}`)),

    /** Kontakt im Outreach-Postfach anlegen oder finden; verschickt nichts. */
    outreachKontakt: (organizerId: number) =>
      daten<{ id: number }>(api.post(`/admin/outreach/contacts/from-organizer/${organizerId}`)),

    // ── Buchungen ───────────────────────────────────────────────────────────────────────
    buchungen: (status: BookingStatus | undefined, page = 0, size = 20) =>
      daten<PageResponse<BookingRequestResponse>>(api.get('/admin/bookings', { params: { status, page, size } })),
    /** Laufende, geplante und bis zu 30 Tage abgelaufene Hervorhebungen. */
    hervorhebungen: (expiredWithinDays = 30) =>
      daten<EventPromotionRow[]>(api.get('/admin/promotions', { params: { expiredWithinDays } })),
    ablehnen: (id: number, comment?: string) =>
      daten<BookingRequestResponse>(api.post(`/admin/bookings/${id}/status`, { status: 'DECLINED', comment })),
    /** Rechnung für den Kunden schreiben und verschicken (DevHub). Danach kein Zurückziehen mehr. */
    rechnungSchicken: (id: number) => daten<BookingRequestResponse>(api.post(`/admin/bookings/${id}/invoice`)),
    /** Bezahlt, aber ohne Rechnung: nachholen. **Nur ADMIN.** Scheitert es, kommt der Grund als 400. */
    rechnungNachholen: (id: number, orgKind?: OrganizerKind) =>
      daten<BookingRequestResponse>(api.post(`/admin/bookings/${id}/paid-invoice`, { orgKind })),
    /** Leistung setzen, wenn die Zahlung schon vermerkt ist. 409 heißt: noch nicht setzbar. */
    freischalten: (id: number, comment?: string, tier?: PromotionTier) =>
      daten<BookingRequestResponse>(api.post(`/admin/bookings/${id}/apply`, { tier, comment })),
    /**
     * Zahlung erhalten: vermerken, Rechnung sichern, freischalten. **Nur ADMIN.**
     * Scheitert nur das Freischalten, antwortet der Server 409 mit der Bestellung im Rumpf; das
     * ist ein Zwischenstand, kein Fehler, und kommt deshalb als Ergebnis zurück statt als Ausnahme.
     */
    zahlungErhalten: async (id: number, comment?: string, orgKind?: OrganizerKind): Promise<ZahlungErgebnis> => {
      try {
        const r = await api.post<BookingRequestResponse>(`/admin/bookings/${id}/paid`, { comment, orgKind });
        return { art: 'freigeschaltet', bestellung: r.data };
      } catch (e) {
        const res = (e as { response?: { status?: number; data?: { error?: string; booking?: BookingRequestResponse } } }).response;
        if (res?.status === 409) {
          return { art: 'zahlung-vermerkt', grund: res.data?.error ?? '', bestellung: res.data?.booking ?? null };
        }
        throw e;
      }
    },

    // ── Preise ──────────────────────────────────────────────────────────────────────────
    preise: () => daten<PriceItem[]>(api.get('/admin/prices')),
    preisAnlegen: (req: PriceItemRequest) => daten<PriceItem>(api.post('/admin/prices', req)),
    preisSpeichern: (id: number, req: PriceItemRequest) => daten<PriceItem>(api.put(`/admin/prices/${id}`, req)),
    /** Schaltet ab; gelöscht wird nie, Anfragen verweisen auf den Preis. */
    preisAbschalten: (id: number) => api.delete(`/admin/prices/${id}`).then(() => undefined),

    // ── Werbeplatz ──────────────────────────────────────────────────────────────────────
    anzeigen: () => daten<AdBannerSetting[]>(api.get('/admin/ad-banners')),
    anzeigeSchalten: (key: string, active: boolean) =>
      daten<AdBannerSetting>(api.put(`/admin/ad-banners/${encodeURIComponent(key)}`, { active })),
    anzeigeArt: (key: string, kind: AdBannerKind) =>
      daten<AdBannerSetting>(api.put(`/admin/ad-banners/${encodeURIComponent(key)}`, { kind })),
    anzeigenZahlen: (days: number) =>
      daten<AdBannerStatsResponse>(api.get('/admin/ad-banners/stats', { params: { days } })),

    // ── Feedback ────────────────────────────────────────────────────────────────────────
    feedback: (page = 0, size = 20, f: FeedbackFilter = {}) => {
      const params: Record<string, unknown> = { page, size };
      if (f.platform) params.platform = f.platform;
      if (f.minRating) params.minRating = f.minRating;
      if (f.withComment) params.withComment = true;
      return daten<PageResponse<Feedback>>(api.get('/feedback', { params }));
    },
    feedbackZahlen: () => daten<FeedbackStats>(api.get('/feedback/stats')),
  };
}

export type VerkaufService = ReturnType<typeof createVerkaufService>;
